import {Component, OnDestroy, OnInit} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ErrorService, UiConfigService} from '@lv1871/core';
import {BehaviorSubject, finalize, Observable, ReplaySubject, Subject, Subscription} from 'rxjs';
import {delay, map, pairwise, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {IAppConfig} from '../../../lib-core/models/appConfig/iapp-config';
import {MatIconRegistry} from '@angular/material/icon';
import {AutofillService} from '../../../lib-dialog-toolbox/services/autofill.service';
import {ClipboardService} from 'ngx-clipboard';
import {EventService} from '../../../lib-dialog-toolbox/services/event.service';
import {ValidationService} from '../../../lib-dialog-toolbox/services/validation.service';
import {UIArtefakt} from '../../../lib-dialog-toolbox/models/uiArtefakt';
import {UiControl} from '../../../lib-dialog-toolbox/models/uiControl';
import momentLib from 'moment/moment';
import {ShellConfigService} from '../../../lib-dialog-toolbox';
import {AntragsdatenserviceRestService} from '../../../lib-dialog-toolbox/services/rest/antragsdatenservice-rest.service';
import {AufzaehlungEntry} from '../../../lib-dialog-toolbox/models/aufzaehlungResponse';

class ReloadResponse {
  reload: string;
}

@Component({
    selector: 'lv-entwicklerleiste',
    templateUrl: './entwicklerleiste.component.html',
    styleUrls: ['./entwicklerleiste.component.scss'],
    standalone: false
})
export class EntwicklerleisteComponent implements OnInit, OnDestroy {

  public successMessage: string;
  public showToolbar: boolean = true;
  public env: IAppConfig;
  public errorMessages: Observable<string[]>;
  public warningMessages: Observable<string[]>;
  public angebotserstellungMoeglichJN$: BehaviorSubject<boolean>;
  public vnr: string;
  public antragsgenerationDatum: string;
  public uiVorgangId: string;
  public uiVorgangIds: string[] = ['Antrag', 'Beitragsrechner'];
  public produkte: string[] = [''];
  public produkteMitKollektivIds: AufzaehlungEntry[];
  public defId: string;
  public kollektivId: number;
  public testdata$: Observable<string[]>;
  public testdata: string = 'DefaultTest';
  public copied = false;
  public copiedCore = false;
  public smsDoi: boolean = false;
  public emailDoi: boolean = false;
  public bonitaet: boolean = false;
  public compliance: boolean = true;
  public isLoading: boolean = true;
  private _reload$ = new Subject<void>();
  private reloadSubscription = Subscription.EMPTY;
  private refreshPage: boolean = false;
  private routeSubscription: Subscription;
  private _secondLastControlSubscription = Subscription.EMPTY;
  private _secondLastControl = new ReplaySubject<UiControl>(1);

  constructor(private _http: HttpClient, public _uiConfigService: UiConfigService, public errorService: ErrorService,
              private matIconRegistry: MatIconRegistry, private _autofillService: AutofillService,
              private _clipboardService: ClipboardService, private _eventService: EventService,
              private _validationService: ValidationService, private _antragsdatenserviceRestService: AntragsdatenserviceRestService,
              private _shellConfigService: ShellConfigService) {
    matIconRegistry.setDefaultFontSetClass('fa');
  }

  ngOnInit(): void {
    this.angebotserstellungMoeglichJN$ = this._eventService.angebotserstellungMoeglichJN$;
    this.errorMessages = this.errorService.errorMessages.pipe(delay(10));
    this.warningMessages = this.errorService.warningMessages.pipe(delay(10));
    this.isLoading = false;
    this.antragsgenerationDatum = this._formatDate(new Date().toString());
    this.env = this._uiConfigService.settings;
    this.uiVorgangId = this._shellConfigService.getParameter().uiVorgangId;

    this._eventService.urlParams$.pipe(
        take(1),
        map(params => params.produktId),
        switchMap(produktId => this._antragsdatenserviceRestService.getAufzaehlung('ProduktID').pipe(
            take(1),
            tap(response => {
                  response.Wert
                      .filter(wert => wert.StringWert !== 'Undefiniert')
                      .forEach(wert => this.produkte.push(wert.StringWert));
                  this.produkteMitKollektivIds = response.Wert;
                  this._setKollektivId(produktId);
                }
            )))
    ).subscribe();

    this.reloadSubscription = this._reload$.pipe(
        tap(load => {
          this.isLoading = true;
        }),
        switchMap(() => this._http.get<ReloadResponse>(this._uiConfigService.getAntragserfassungUrl() + '/api/produkt/reload')),
        switchMap(res => this._http.post(this._uiConfigService.getAntragserfassungUrl() + '/api/cache/invalidate', {}).pipe(map(_ => res))),
        tap(res => {
          this.isLoading = false;
          this.successMessage = res.reload;
          setTimeout(_ => {
            this.successMessage = null;
            if (this.refreshPage) {
              this._eventService.supressBeforeUnloadDialogButtonClicked$.next();
              window.location.reload();
            }
          }, 2000);
        })
    ).subscribe();

    this.routeSubscription = this._eventService.urlParams$
        .subscribe(params => {
              this.defId = params.produktId;
              if (!!params.antragsgenerationDatum) {
                this.antragsgenerationDatum = params.antragsgenerationDatum;
              }
              if (!!params.kollektivId) {
                this.kollektivId = Number(params.kollektivId);
              }
              this.smsDoi = params.smsDoi;
              this.emailDoi = params.emailDoi;
              this.bonitaet = params.bonitaet;
              this.compliance = params.compliance;
              this.uiVorgangId = params.uiVorgangId;
            }
        );
    this.testdata$ = this._autofillService.getTestData();

    this._secondLastControl.next({} as UiControl);
    this._secondLastControlSubscription = this._eventService.uiControls$.pipe(
        pairwise(),
        map(([a, _]) => a)
    ).subscribe(this._secondLastControl);
  }

  reloadLivesApi() {
    this._reload$.next();
  }

  ngOnDestroy(): void {
    this.refreshPage = false;
    this.reloadSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
    this._secondLastControlSubscription.unsubscribe();
  }

  clearWarnings() {
    this.errorService.clearWarnings();
  }

  clearErrors() {
    this.errorService.clearErrors();
  }

  getProdukteFiltered(): string[] {
    return this.produkte.filter(produkt => !this.defId || produkt.toLowerCase().includes(this.defId?.toLowerCase()))
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }

  setProdukt(produkt: any) {
    this.defId = produkt;
    this._setKollektivId(produkt);
  }

  reloadLivesApiAndPage() {
    this.refreshPage = true;
    this._reload$.next();
  }

  reloadLivesApiWithParams() {
    let url = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
    const vorgangId = this.uiVorgangId;

    if (this.vnr) {
      url += '?versicherungsnummer=' + this.vnr;
    } else if (this.defId) {
      url += '?produktId=' + this._stripKollektivErweiterung(this.defId);
    }
    url += '&vorgangId=' + vorgangId;
    url += '&smsDoi=' + this.smsDoi;
    url += '&emailDoi=' + this.emailDoi;
    url += '&bonitaet=' + this.bonitaet;
    url += '&compliance=' + this.compliance;
    url += '&antragsgenerationDatum=' + this._formatDate(this.antragsgenerationDatum);
    if (!!this.kollektivId) {
      url += '&kollektivId=' + this.kollektivId;
    }
    this._eventService.supressBeforeUnloadDialogButtonClicked$.next();
    if (url === window.location.href) {
      window.location.reload();
    }
    window.location.replace(url);
  }

  clearDropdownInput() {
    this.defId = undefined;
  }

  autofill() {
    this.isLoading = true;
    this._autofillService.autofill(this.testdata).pipe(
        finalize(() => this.isLoading = false)).subscribe();
  }

  validate() {
    this._validationService.validateAllForms();
  }

  copyJsonToClipboard() {
    this._eventService.uiControls$.pipe(
        take(1),
        withLatestFrom(this._secondLastControl),
        map(([last, secondLast]) => {
          if (last.uiVorgang?.UIArtefakt?.length > 0) {
            return last;
          }
          return secondLast;
        })
    ).subscribe(control => {
      if (this._clipboardService.copyFromContent(JSON.stringify(control, null, 2))) {
        this.copied = true;
        setTimeout(() => this.copied = false, 2000);
      }
    });
  }

  copyCoreJsonToClipboard() {
    this._eventService.uiControls$.pipe(
        take(1),
        withLatestFrom(this._secondLastControl),
        map(([last, secondLast]) => {
          if (last.uiVorgang?.UIArtefakt?.length > 0) {
            return last;
          }
          return secondLast;
        })
    ).subscribe(control => {
      const coreJson = {
        UIAuftrag: {
          Auftragstyp: 'Simulieren',
          UIAuftragVertrag: {
            ProduktID: !!control.produktId ? control.produktId : 'SBU',
            AntragsgenerationDatum: control.antragsgenerationDatum,
            Vertragsstatus: 'AntragNichtEingereicht'
          },
          UITeilauftrag: [
            {
              ArbeitsschrittID: 'NeugeschaeftPolicieren',
              WirkDat: this._getNextMonthFirstDay()
            }
          ]
        },
        UIVorgang: {
          ...control.uiVorgang,
          UIArtefakt: this._clearForCore(control.uiVorgang.UIArtefakt)
        }
      };
      if (this._clipboardService.copyFromContent(JSON.stringify(coreJson, null, 2))) {
        this.copiedCore = true;
        setTimeout(() => this.copiedCore = false, 2000);
      }
    });
  }

  private _setKollektivId(produkt: any) {
    const kollektivId = this.produkteMitKollektivIds.filter(produktMitKollektivId =>
        produktMitKollektivId.StringWert === produkt)[0]?.kollektivId;
    this.kollektivId = !!kollektivId ? kollektivId : null;
  }

  private _getNextMonthFirstDay(): string {
    const today = new Date();
    let next: Date;
    if (today.getMonth() === 11) {
      next = new Date(today.getFullYear() + 1, 0, 1);
    } else {
      next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    }
    return momentLib(next).format('YYYY-MM-DD');
  }

  private _clearForCore(artefakte: UIArtefakt[]) {
    if (!artefakte) {
      return artefakte;
    }
    const filteredArtefakte = artefakte.filter(artefakt => !artefakt['ReferenzIdentifikator']);
    filteredArtefakte.forEach(artefakt => {
      if (artefakt.UIArtefakttyp === 'Risikosuchfrage') {
        artefakt.UIArtefakt = null;
      } else if (!!artefakt['Quellsystemservice']) {
        artefakt.UIArtefakt = null;
      } else {
        if (artefakt.WertEingabe instanceof Object) {
          artefakt.WertEingabe = null;
        }
        artefakt.UIArtefakt = this._clearForCore(artefakt.UIArtefakt);
      }
    });
    return filteredArtefakte;
  }

  private _formatDate(datum: string) {
    return momentLib(datum).hour(12).format('YYYY-MM-DD');
  }

  private _stripKollektivErweiterung(defId: string): string {
    if (!defId || !(defId.includes('(') && defId.endsWith(')'))) {
      return defId;
    }
    return defId.substring(0, defId.indexOf('('));
  }
}

